import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { extractMemoryFacts } from "./aria.server";
import { isSameEvent, betterCard } from "./signal-fingerprint";
import { dismissTwins } from "./signal-dedupe.server";
import { getConnectionMetaForUser } from "./app-user-connections.server";
import { dedupeEvents, meetingKey } from "./calendar-dedupe";

export const getCalendarStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId, supabase } = context;
    const meta = await getConnectionMetaForUser(userId, "google_calendar");
    // Which Google account are we actually reading? Surfaces second-account mixups.
    let accountEmail: string | null = null;
    if (meta) {
      const { data: row } = await supabase
        .from("calendar_events")
        .select("calendar_name")
        .eq("user_id", userId)
        .not("calendar_name", "is", null)
        .like("calendar_name", "%@%")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      accountEmail = (row?.calendar_name as string) ?? null;
    }
    return {
      connected: !!meta,
      connectedAt: meta?.connectedAt ?? null,
      lastSyncedAt: meta?.lastSyncedAt ?? null,
      lastSyncError: meta?.lastSyncError ?? null,
      breakKind: meta?.breakKind ?? ("none" as const),
      accountEmail,
      autoSync: true,

    };
  });


export const syncGoogleCalendar = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        daysPast: z.number().int().min(0).max(60).default(7),
        daysFuture: z.number().int().min(0).max(60).default(14),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { runCalendarSync } = await import("./calendar-sync.server");
    const { markConnectionSynced } = await import("./app-user-connections.server");
    const { recordCalendarSyncRun } = await import("./sync-history.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const startedAt = new Date().toISOString();
    try {
      const result = await runCalendarSync(context.supabase, context.userId, data);
      await markConnectionSynced(context.userId, "google_calendar", null);
      await recordCalendarSyncRun(supabaseAdmin, context.userId, "manual", startedAt, result);
      return result;
    } catch (e: any) {
      await markConnectionSynced(context.userId, "google_calendar", String(e?.message ?? e));
      await recordCalendarSyncRun(
        supabaseAdmin,
        context.userId,
        "manual",
        startedAt,
        null,
        String(e?.message ?? e),
      );
      throw e;
    }
  });

/**
 * Read-only: what Google actually returns for the signed-in user's calendars.
 * Writes nothing — used to answer "why isn't my meeting in Pyng?".
 */
export const diagnoseCalendarSync = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { diagnoseCalendars } = await import("./calendar-sync.server");
    return diagnoseCalendars(context.userId, { daysPast: 7, daysFuture: 14 });
  });

/** Every calendar on the connected account, with the member's on/off choice. */
export const listGoogleCalendars = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { listAvailableCalendars } = await import("./calendar-sync.server");
    return listAvailableCalendars(context.supabase, context.userId);
  });

/** Turn a single calendar on or off. The caller resyncs right after. */
export const setCalendarEnabled = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        calendarId: z.string().min(1),
        calendarName: z.string().nullable().optional(),
        enabled: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.from("calendar_prefs").upsert(
      {
        user_id: context.userId,
        calendar_id: data.calendarId,
        calendar_name: data.calendarName ?? null,
        enabled: data.enabled,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,calendar_id" },
    );
    if (error) throw error;
    return { ok: true };
  });

export const listUpcomingEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("calendar_events")
      .select("id, title, start_at, end_at, attendees, meeting_url, location")
      .eq("user_id", userId)
      .gte("end_at", new Date().toISOString())
      .order("start_at", { ascending: true })
      .limit(20);
    if (error) throw error;
    return { events: data ?? [] };
  });

export const listPendingHooks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("hooks")
      .select(
        "id, contact_id, trigger_type, title, body, context, scheduled_for, calendar_event_id, contact:contacts(id, name, initials, photo_url, title, company)",
      )
      .eq("user_id", userId)
      .eq("status", "pending")
      .neq("trigger_type", "fresh_link")
      .order("scheduled_for", { ascending: false })
      .limit(20);
    if (error) throw error;
    // Post-meeting prompts can repeat, so collapse exact duplicates by person + body.
    const seen = new Set<string>();
    const hooks: any[] = [];
    for (const h of (data ?? []) as any[]) {
      const key = `${h.contact_id ?? "none"}|${String(h.body ?? h.title ?? "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 160)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      hooks.push(h);
    }
    return { hooks };
  });

export const listPendingFreshLinkHooks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("hooks")
      .select(
        "id, contact_id, trigger_type, title, body, context, scheduled_for, calendar_event_id, contact:contacts(id, name, initials, photo_url, title, company)",
      )
      .eq("user_id", userId)
      .eq("status", "pending")
      .eq("trigger_type", "fresh_link")
      .order("scheduled_for", { ascending: false })
      .limit(50);
    if (error) throw error;

    // Page-change hooks are only one source of "worth remembering" material.
    // Signals found during a Boost or a new-person scan never became hooks, so
    // they used to sit invisibly on the profile. Fold recent, undismissed
    // signals into the same deck as synthetic cards (`signal:<uuid>`).
    const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { data: signalRows } = await supabase
      .from("person_signals")
      .select(
        "id, contact_id, kind, summary, source_url, detected_at, contact:contacts(id, name, initials, photo_url, title, company)",
      )
      .eq("user_id", userId)
      .is("dismissed_at", null)
      .gte("detected_at", since)
      .order("detected_at", { ascending: false })
      .limit(50);

    // Quality filters that used to live in the separate training tray: drop
    // muted signal kinds and anything already saved as a memory.
    const { data: profileRow } = await supabase
      .from("profiles")
      .select("signal_mute_kinds")
      .eq("id", userId)
      .maybeSingle();
    const mutedKinds = new Set<string>(
      Array.isArray(profileRow?.signal_mute_kinds) ? (profileRow!.signal_mute_kinds as string[]) : [],
    );
    const signalIds = ((signalRows ?? []) as any[]).map((s) => `signal:${s.id}`);
    let savedSources = new Set<string>();
    if (signalIds.length) {
      const { data: mems } = await supabase
        .from("memories")
        .select("source")
        .eq("user_id", userId)
        .in("source", signalIds);
      savedSources = new Set(((mems ?? []) as any[]).map((m) => String(m.source)));
    }
    const usableSignals = ((signalRows ?? []) as any[]).filter(
      (s) => !mutedKinds.has(s.kind) && !savedSources.has(`signal:${s.id}`),
    );

    const norm = (s: unknown) =>
      String(s ?? "").toLowerCase().replace(/\s+/g, " ").trim().slice(0, 160);

    const cards: any[] = [];
    const seen = new Set<string>();
    const push = (card: any, dedupeKey: string) => {
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      cards.push(card);
    };

    // Hooks first — they carry the richer body and can be marked done directly.
    for (const h of (data ?? []) as any[]) {
      push(h, `${h.contact_id ?? "none"}|${norm(h.body ?? h.title)}`);
    }
    for (const s of usableSignals) {
      push(
        {
          id: `signal:${s.id}`,
          contact_id: s.contact_id,
          trigger_type: "fresh_link",
          title: s.contact?.name ?? "New signal",
          body: s.summary,
          context: { signal_kind: s.kind, source_url: s.source_url },
          scheduled_for: s.detected_at,
          calendar_event_id: null,
          contact: s.contact ?? null,
        },
        `${s.contact_id ?? "none"}|${norm(s.summary)}`,
      );
    }

    cards.sort(
      (a, b) =>
        new Date(b.scheduled_for ?? 0).getTime() - new Date(a.scheduled_for ?? 0).getTime(),
    );

    // Collapse near-duplicate write-ups of the same event for the same person.
    // Exact-string dedupe above only catches identical wording; two articles
    // about one award otherwise become two cards.
    const collapsed: any[] = [];
    for (const card of cards) {
      const kind = card.context?.signal_kind ?? null;
      const summary = String(card.body ?? card.title ?? "");
      const idx = collapsed.findIndex(
        (k) =>
          (k.contact_id ?? "none") === (card.contact_id ?? "none") &&
          isSameEvent(
            { kind: k.context?.signal_kind ?? null, summary: String(k.body ?? k.title ?? "") },
            { kind, summary },
          ),
      );
      if (idx === -1) collapsed.push(card);
      else collapsed[idx] = betterCard(collapsed[idx], card);
    }

    // Round-robin across people so one person with many signals can't take over
    // the whole deck. Each pass takes the next-newest card per contact.
    const byContact = new Map<string, any[]>();
    for (const c of collapsed) {
      const key = c.contact_id ?? "none";
      const list = byContact.get(key);
      if (list) list.push(c);
      else byContact.set(key, [c]);
    }
    const groups = [...byContact.values()];
    const interleaved: any[] = [];
    for (let i = 0; interleaved.length < collapsed.length; i++) {
      let added = false;
      for (const g of groups) {
        if (g[i]) {
          interleaved.push(g[i]);
          added = true;
        }
      }
      if (!added) break;
    }
    return { hooks: interleaved.slice(0, 20) };
  });

export const updateHookStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        // Accepts a hook uuid or a synthetic `signal:<uuid>` card id.
        id: z.string().regex(/^(signal:)?[0-9a-f-]{36}$/i),
        status: z.enum(["done", "dismissed", "snoozed"]),
        notRelevant: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();

    // Signal-backed cards have no hook row — dismiss the signal instead.
    if (data.id.startsWith("signal:")) {
      const signalId = data.id.slice("signal:".length);
      const { data: sig } = await supabase
        .from("person_signals")
        .select("id, contact_id, kind, summary")
        .eq("id", signalId)
        .eq("user_id", userId)
        .maybeSingle();
      if (!sig) return { ok: true };
      await supabase
        .from("person_signals")
        .update({
          dismissed_at: nowIso,
          dismiss_reason: data.status === "done" ? "remembered" : data.notRelevant ? "uninteresting" : "stale",
        })
        .eq("id", signalId)
        .eq("user_id", userId);
      // "Keep" = remember: the fact lives on in the person's memory.
      if (data.status === "done" && sig.contact_id && sig.summary) {
        const { count } = await supabase
          .from("memories")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("source", `signal:${signalId}`);
        if (!count) {
          await supabase.from("memories").insert({
            user_id: userId,
            contact_id: sig.contact_id,
            fact: String(sig.summary).slice(0, 500),
            confidence: 0.8,
            source: `signal:${signalId}`,
          });
        }
      }
      // Retire other write-ups of the same event so the twin can't reappear.
      if (sig.contact_id) {
        await dismissTwins(supabase, userId, sig.contact_id, {
          kind: (sig as any).kind ?? null,
          summary: String((sig as any).summary ?? ""),
          excludeId: signalId,
          nowIso,
        });
      }
      if (data.notRelevant && sig.contact_id) {
        await supabase.from("hook_feedback").insert({
          user_id: userId,
          contact_id: sig.contact_id,
          trigger_type: "fresh_link",
          action: "not_relevant",
        });
      }
      return { ok: true };
    }

    const { data: hookRow } = await supabase
      .from("hooks")
      .select("id, contact_id, trigger_type, body, title")
      .eq("id", data.id)
      .eq("user_id", userId)
      .maybeSingle();
    const patch = {
      status: data.status,
      completed_at: data.status === "done" ? nowIso : null,
      dismissed_at: data.status === "dismissed" ? nowIso : null,
    };
    const { error } = await supabase
      .from("hooks")
      .update(patch)
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw error;

    if (data.notRelevant && hookRow?.contact_id) {
      // Teach the ranker this kind of prompt missed for this person.
      await supabase.from("hook_feedback").insert({
        user_id: userId,
        contact_id: hookRow.contact_id,
        trigger_type: hookRow.trigger_type,
        action: "not_relevant",
      });
      // Retire only the signal this prompt was built from. A prefix `ilike`
      // sweep used to retire every signal whose summary merely started the
      // same way, quietly wiping a person's whole deck in one tap.
      const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
      const body = norm(String(hookRow.body ?? ""));
      if (body.length > 20) {
        const { data: candidates } = await supabase
          .from("person_signals")
          .select("id, summary")
          .eq("user_id", userId)
          .eq("contact_id", hookRow.contact_id)
          .is("dismissed_at", null)
          .limit(100);
        const match = (candidates ?? []).find((s: any) => {
          const summary = norm(String(s.summary ?? ""));
          if (!summary) return false;
          // The hook body is the signal summary, possibly truncated.
          return summary === body || summary.startsWith(body) || body.startsWith(summary);
        });
      if (match) {
          await supabase
            .from("person_signals")
            .update({ dismissed_at: nowIso })
            .eq("id", match.id)
            .eq("user_id", userId);
        }
      }
    }

    // Whichever way the card was resolved, drop near-duplicate signals about
    // the same event for this person.
    if (hookRow?.contact_id) {
      await dismissTwins(supabase, userId, hookRow.contact_id, {
        kind: null,
        summary: String(hookRow.body ?? hookRow.title ?? ""),
        nowIso,
      });
    }
    return { ok: true };
  });

export const saveHookMemory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        // Hook uuid, or a synthetic `signal:<uuid>` card id from the deck.
        hookId: z.string().regex(/^(signal:)?[0-9a-f-]{36}$/i),
        contactId: z.string().uuid(),
        text: z.string().trim().min(2).max(2000).optional(),
        calendarEventId: z.string().uuid().optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();
    const isSignal = data.hookId.startsWith("signal:");

    // Both card kinds resolve to the same shape: some source text plus an
    // optional calendar event, and the source row gets retired once saved.
    let hookRow: { body: string | null; title: string | null; calendar_event_id: string | null };

    if (isSignal) {
      const signalId = data.hookId.slice("signal:".length);
      const { data: sig } = await supabase
        .from("person_signals")
        .select("id, contact_id, summary")
        .eq("id", signalId)
        .eq("user_id", userId)
        .maybeSingle();
      if (!sig) throw new Error("Signal not found");
      if (sig.contact_id !== data.contactId) throw new Error("Contact mismatch");
      hookRow = { body: sig.summary, title: null, calendar_event_id: null };
      await supabase
        .from("person_signals")
        .update({ dismissed_at: nowIso })
        .eq("id", signalId)
        .eq("user_id", userId);
    } else {
      const { data: row } = await supabase
        .from("hooks")
        .select("id, contact_id, trigger_type, body, title, calendar_event_id")
        .eq("id", data.hookId)
        .eq("user_id", userId)
        .maybeSingle();
      if (!row) throw new Error("Hook not found");
      if (row.contact_id !== data.contactId) throw new Error("Contact mismatch");
      hookRow = row;
      const { error: hookError } = await supabase
        .from("hooks")
        .update({ status: "done", completed_at: nowIso })
        .eq("id", data.hookId)
        .eq("user_id", userId);
      if (hookError) throw hookError;
    }

    // Determine what to store as memory fact(s).
    const base = data.text?.trim() || String(hookRow.body ?? hookRow.title ?? "").trim();
    if (!base) return { ok: true, count: 0 };

    let facts: Array<{ fact: string; confidence: number; source: string }>;
    const looksAtomic = base.length < 140 && !/[.;]\s+\S/.test(base);
    if (looksAtomic || data.text) {
      // If the user explicitly typed a note, prefer it as a single manual fact
      // unless it's long enough to be worth splitting.
      facts = [{ fact: base, confidence: 0.9, source: "manual" }];
    } else {
      try {
        const extracted = await extractMemoryFacts(base);
        facts = extracted.length
          ? extracted.map((f) => ({ fact: f.fact, confidence: Math.max(0, Math.min(1, f.confidence ?? 0.7)), source: "ai" }))
          : [{ fact: base.slice(0, 200), confidence: 0.7, source: "manual" }];
      } catch (e) {
        console.error("memory extraction failed", e);
        facts = [{ fact: base.slice(0, 200), confidence: 0.7, source: "manual" }];
      }
    }

    const rows = facts.map((f) => ({ user_id: userId, contact_id: data.contactId, ...f }));
    const { error: memError } = await supabase.from("memories").insert(rows);
    if (memError) throw memError;

    await supabase.from("interactions").insert({
      user_id: userId,
      contact_id: data.contactId,
      type: data.calendarEventId || hookRow.calendar_event_id ? "meeting" : "note",
      summary: base.slice(0, 200),
      occurred_at: nowIso,
      calendar_event_id: data.calendarEventId ?? hookRow.calendar_event_id ?? null,
    });

    await supabase
      .from("contacts")
      .update({ last_interaction_at: nowIso })
      .eq("id", data.contactId)
      .eq("user_id", userId)
      .or("last_interaction_at.is.null,last_interaction_at.lt.${nowIso}");

    return { ok: true, count: facts.length };
  });

/**
 * Post-meeting recap: one card per calendar event that ended in the last 72h
 * and hasn't been captured or dismissed yet. Meetings with nobody from the
 * orbit are included too, so nothing gets lost.
 */
const RECAP_WINDOW_MS = 72 * 3600_000;

export const listPostMeetingRecaps = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const now = Date.now();
    const fromIso = new Date(now - RECAP_WINDOW_MS).toISOString();
    const nowIso = new Date(now).toISOString();

    const { data: events, error } = await supabase
      .from("calendar_events")
      .select("id, title, start_at, end_at, attendees, location, matched_contact_ids, status, created_at")
      .eq("user_id", userId)
      .gte("end_at", fromIso)
      .lt("end_at", nowIso)
      .order("end_at", { ascending: false })
      .limit(12);
    if (error) throw error;
    // Collapse duplicate Google copies of the same meeting into one recap card.
    const groups = dedupeEvents((events ?? []).filter((e: any) => e.status !== "cancelled"));
    const live = groups.map((g) => g.primary);
    if (live.length === 0) return { recaps: [] as any[] };

    // A resolution on ANY copy resolves the whole group.
    const eventIds = groups.flatMap((g) => g.ids);
    const groupByPrimary = new Map<string, string[]>(
      groups.map((g) => [g.primary.id as string, g.ids]),
    );
    const [{ data: resolvedHooks }, { data: logged }] = await Promise.all([
      supabase
        .from("hooks")
        .select("calendar_event_id, status")
        .eq("user_id", userId)
        .in("calendar_event_id", eventIds)
        .neq("status", "pending"),
      supabase
        .from("interactions")
        .select("calendar_event_id")
        .eq("user_id", userId)
        .in("calendar_event_id", eventIds),
    ]);
    const resolved = new Set<string>();
    for (const h of resolvedHooks ?? []) if (h.calendar_event_id) resolved.add(h.calendar_event_id);
    for (const i of logged ?? []) if (i.calendar_event_id) resolved.add(i.calendar_event_id);

    const open = live.filter(
      (e: any) => !(groupByPrimary.get(e.id) ?? [e.id]).some((id) => resolved.has(id)),
    );
    if (open.length === 0) return { recaps: [] as any[] };

    const contactIds = Array.from(
      new Set<string>(open.flatMap((e: any) => ((e.matched_contact_ids ?? []) as string[]))),
    );
    let peopleById = new Map<string, any>();
    if (contactIds.length) {
      const { data: contacts } = await supabase
        .from("contacts")
        .select("id, name, initials, photo_url, title, company")
        .eq("user_id", userId)
        .in("id", contactIds);
      peopleById = new Map((contacts ?? []).map((c: any) => [c.id, c]));
    }

    const recaps = open.map((e: any) => ({
      eventId: e.id,
      title: e.title ?? "(no title)",
      startAt: e.start_at,
      endAt: e.end_at,
      location: e.location ?? null,
      people: ((e.matched_contact_ids ?? []) as string[])
        .map((id) => peopleById.get(id))
        .filter(Boolean),
      attendees: (Array.isArray(e.attendees) ? e.attendees : [])
        .slice(0, 8)
        .map((a: any) => ({ name: a?.displayName ?? a?.name ?? null, email: a?.email ?? null })),
    }));
    return { recaps };
  });

export const savePostMeetingRecap = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        calendarEventId: z.string().uuid(),
        notes: z.string().trim().max(4000).optional(),
        decisions: z.string().trim().max(4000).optional(),
        links: z.array(z.string().trim().url().max(500)).max(8).default([]),
        followUps: z
          .array(
            z.object({
              text: z.string().trim().min(2).max(200),
              dueAt: z.string().datetime(),
              contactId: z.string().uuid().optional(),
            }),
          )
          .max(6)
          .default([]),
        dismissed: z.boolean().default(false),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const nowIso = new Date().toISOString();

    const { data: event } = await supabase
      .from("calendar_events")
      .select("id, title, start_at, attendees, matched_contact_ids")
      .eq("id", data.calendarEventId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!event) throw new Error("Meeting not found");

    // Duplicate Google copies of this meeting must resolve together.
    const { data: siblings } = await supabase
      .from("calendar_events")
      .select("id, title, start_at, attendees, matched_contact_ids")
      .eq("user_id", userId)
      .eq("start_at", event.start_at);
    const key = meetingKey(event);
    const eventIds = Array.from(
      new Set<string>([
        data.calendarEventId,
        ...((siblings ?? []) as any[]).filter((s) => meetingKey(s) === key).map((s) => s.id as string),
      ]),
    );
    const people = Array.from(
      new Set<string>(
        ((siblings ?? []) as any[])
          .filter((s) => eventIds.includes(s.id))
          .flatMap((s) => ((s.matched_contact_ids ?? []) as string[])),
      ),
    ).filter(Boolean);

    // Retire any pending hooks tied to this meeting either way.
    const resolveHooks = async (status: "done" | "dismissed") => {
      const { data: rows } = await supabase
        .from("hooks")
        .select("id")
        .eq("user_id", userId)
        .in("calendar_event_id", eventIds)
        .eq("status", "pending");
      if (rows?.length) {
        await supabase
          .from("hooks")
          .update({
            status,
            completed_at: status === "done" ? nowIso : null,
            dismissed_at: status === "dismissed" ? nowIso : null,
          })
          .eq("user_id", userId)
          .in("calendar_event_id", eventIds)
          .eq("status", "pending");
      } else {
        // No hook existed (e.g. unmatched meeting) — leave a resolved marker so
        // the card doesn't come back on the next sync.
        await supabase.from("hooks").insert({
          user_id: userId,
          contact_id: null,
          trigger_type: "post_meeting",
          status,
          title: event.title ?? "Meeting recap",
          body: null,
          calendar_event_id: data.calendarEventId,
          scheduled_for: nowIso,
          completed_at: status === "done" ? nowIso : null,
          dismissed_at: status === "dismissed" ? nowIso : null,
        });
      }
    };

    if (data.dismissed) {
      await resolveHooks("dismissed");
      return { ok: true, memories: 0, reminders: 0 };
    }

    const notes = data.notes?.trim() ?? "";
    const decisions = data.decisions?.trim() ?? "";
    const summary = (notes || decisions || event.title || "Meeting").slice(0, 200);

    const factTexts: string[] = [];
    // Same memory quality as the Add-context drawer: long free-form notes get
    // AI-split into atomic facts instead of stored as one wall of text.
    const looksAtomic = notes.length < 140 && !/[.;]\s+\S/.test(notes);
    if (notes && !looksAtomic) {
      try {
        const extracted = await extractMemoryFacts(notes);
        for (const f of extracted) {
          const t = f.fact?.trim();
          if (t && t.length > 2) factTexts.push(t);
        }
      } catch (e) {
        console.error("recap fact extraction failed", e);
      }
    }
    if (!factTexts.length) {
      for (const line of notes.split(/\n+/)) {
        const t = line.trim();
        if (t.length > 2) factTexts.push(t);
      }
    }
    for (const line of decisions.split(/\n+/)) {
      const t = line.trim();
      if (t.length > 2) factTexts.push(`Decided: ${t}`);
    }

    let memoryCount = 0;
    if (people.length && factTexts.length) {
      const rows = people.flatMap((contactId) =>
        factTexts.map((fact) => ({
          user_id: userId,
          contact_id: contactId,
          fact: fact.slice(0, 500),
          confidence: 0.9,
          source: "manual",
        })),
      );
      const { error: memErr } = await supabase.from("memories").insert(rows);
      if (memErr) throw memErr;
      memoryCount = rows.length;
      // What was said in the room should change what we watch for.
      try {
        const { registerFactTopics } = await import("@/lib/memory-topics.server");
        for (const contactId of people) {
          await registerFactTopics(supabase, userId, contactId, factTexts);
        }
      } catch (e) {
        console.error("recap topic registration failed", e);
      }
    }

    // Links pasted while capturing become watched sources on every attendee.
    let linkCount = 0;
    if (people.length && data.links.length) {
      const unscrapable = (u: string) =>
        /(^|\.)(linkedin\.com|facebook\.com|instagram\.com|x\.com|twitter\.com)$/i.test(
          (() => {
            try {
              return new URL(u).hostname;
            } catch {
              return "";
            }
          })(),
        );
      const { data: existing } = await supabase
        .from("contact_links")
        .select("contact_id, url")
        .eq("user_id", userId)
        .in("contact_id", people);
      const have = new Set((existing ?? []).map((r: any) => `${r.contact_id}|${r.url}`));
      const rows = people.flatMap((contactId) =>
        data.links
          .filter((url) => !have.has(`${contactId}|${url}`))
          .map((url) => ({
            user_id: userId,
            contact_id: contactId,
            kind: "other",
            url,
            monitorable: !unscrapable(url),
          })),
      );
      if (rows.length) {
        const { error: linkErr } = await supabase.from("contact_links").insert(rows);
        if (linkErr) throw linkErr;
        linkCount = rows.length;
      }
    }

    if (people.length) {
      const others = (id: string) => people.filter((p) => p !== id);
      const interactionRows = people.map((contactId) => ({
        user_id: userId,
        contact_id: contactId,
        type: "meeting" as const,
        summary,
        occurred_at: nowIso,
        calendar_event_id: data.calendarEventId,
        linked_contact_ids: others(contactId),
      }));
      const { error: intErr } = await supabase.from("interactions").insert(interactionRows);
      if (intErr) throw intErr;
      await supabase
        .from("contacts")
        .update({ last_interaction_at: nowIso })
        .eq("user_id", userId)
        .in("id", people);
    }

    let reminderCount = 0;
    const followUps = data.followUps.filter((f) => f.text.trim().length > 1);
    if (followUps.length) {
      const rows = followUps
        .map((f) => ({
          user_id: userId,
          contact_id: f.contactId ?? people[0] ?? null,
          title: f.text.trim().slice(0, 200),
          due_at: f.dueAt,
        }))
        .filter((r) => !!r.contact_id) as Array<{
        user_id: string;
        contact_id: string;
        title: string;
        due_at: string;
      }>;
      if (rows.length) {
        const { error: remErr } = await supabase.from("reminders").insert(rows);
        if (remErr) throw remErr;
        reminderCount = rows.length;
      }
    }

    await resolveHooks("done");
    return { ok: true, memories: memoryCount, reminders: reminderCount, links: linkCount };
  });
